import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Search24Regular, Link24Regular, Copy24Regular } from '@fluentui/react-icons';
import { callExternalApi } from '@repo/front-end-services';
import { useUserStore } from '@repo/front-end-state';
import Config from '../../common/Config';
import { Tooltip } from '@repo/ui';
import { CopyToClipboard } from 'react-copy-to-clipboard';

interface ManufacturerProduct {
  default_capacity: string | null;
  manufacturer: string | null;
  model_3d: string | null;
  product: string | null;
  product_line: string | null;
  reference_id: string | null;
  reference_url: string | null;
}

interface ManufacturerProductsAutocompleteProps {
  onSelect?: (product: ManufacturerProduct) => void;
  placeholder?: string;
  className?: string;
}

const ManufacturerProductsAutocomplete: React.FC<ManufacturerProductsAutocompleteProps> = ({
  onSelect,
  placeholder = 'Search manufacturer products...',
  className,
}) => {
  const { authMetadata } = useUserStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<ManufacturerProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Debounced search function
  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        if (term.trim().length < 2) {
          setResults([]);
          setIsOpen(false);
          return;
        }

        if (!authMetadata?.tokens?.accessToken) {
          console.error('No access token available');
          setResults([]);
          return;
        }

        setIsLoading(true);
        try {
          const response = await callExternalApi({
            config: {
              method: 'GET',
              url: `${Config.middlewareApi}api/manufacturer-products/search-all`,
              params: { q: term.trim() },
              headers: {
                Authorization: `Bearer ${authMetadata?.tokens?.accessToken}`,
              },
            },
          });

          if (response.error) {
            console.error('Search error:', response.error);
            setResults([]);
          } else {
            // Type assertion since we know the API returns an array of ManufacturerProduct
            const products = (response.data as unknown as ManufacturerProduct[]) || [];
            setResults(products);
            setIsOpen(true);
          }
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300); // 300ms debounce
    },
    [authMetadata?.tokens?.accessToken],
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    debouncedSearch(value);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle selection
  const handleSelect = (product: ManufacturerProduct) => {
    setSearchTerm(product.product || '');
    setIsOpen(false);
    setSelectedIndex(-1);
    onSelect?.(product);
  };

  // Handle copy reference ID
  const handleCopyReferenceId = (referenceId: string, e?: React.MouseEvent) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
      e.preventDefault();
    }

    const trimmedReferenceId = referenceId.trim();

    // Show feedback immediately
    setCopiedId(trimmedReferenceId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Scroll dropdown into view when it opens
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if dropdown extends beyond viewport
      if (rect.bottom > viewportHeight) {
        // Scroll the dropdown into view
        dropdown.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [isOpen, results]);

  const formatProductDisplay = (product: ManufacturerProduct) => {
    const parts = [];
    if (product.manufacturer) parts.push(product.manufacturer);
    if (product.product) parts.push(product.product);
    if (product.product_line) parts.push(`(${product.product_line})`);
    return parts.join(' - ');
  };

  return (
    <AutocompleteContainer className={className}>
      <InputContainer>
        <SearchIcon>
          <Search24Regular />
        </SearchIcon>
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            // Delay closing to allow clicks on dropdown items
            setTimeout(() => {
              setIsOpen(false);
              setSelectedIndex(-1);
            }, 150);
          }}
          placeholder={placeholder}
        />
        {isLoading && <LoadingSpinner />}
      </InputContainer>

      {isOpen && results.length > 0 && (
        <Dropdown ref={dropdownRef}>
          <SearchHint>
            {results.length} product{results.length !== 1 ? 's' : ''} found
          </SearchHint>
          {results.map((product, index) => {
            return (
              <DropdownItem
                key={`${product.manufacturer}-${product.product}-${index}`}
                className={index === selectedIndex ? 'selected' : ''}
                onClick={() => handleSelect(product)}
              >
                <ProductInfo>
                  <ProductName>{formatProductDisplay(product)}</ProductName>
                  <ProductDetailRow>
                    <ProductDetail>
                      {product.model_3d && product.reference_id
                        ? `${product.model_3d} • ID: ${product.reference_id}`
                        : product.model_3d ||
                          (product.reference_id ? `ID: ${product.reference_id}` : '')}
                    </ProductDetail>
                    <ActionButtons>
                      {product.reference_id && (
                        <CopyToClipboard
                          text={product.reference_id}
                          onCopy={() => {
                            handleCopyReferenceId(product.reference_id!);
                          }}
                        >
                          <CopyButton type="button" $copied={copiedId === product.reference_id}>
                            <Tooltip text="Copy Reference ID" className="tooltip" />
                            <Copy24Regular />
                            {copiedId === product.reference_id && (
                              <CopyFeedback>Reference ID Copied</CopyFeedback>
                            )}
                          </CopyButton>
                        </CopyToClipboard>
                      )}
                      {product.reference_url && (
                        <ReferenceLink
                          href={product.reference_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >
                          <Tooltip text="Open URL" className="tooltip" />
                          <Link24Regular />
                        </ReferenceLink>
                      )}
                    </ActionButtons>
                  </ProductDetailRow>
                </ProductInfo>
              </DropdownItem>
            );
          })}
        </Dropdown>
      )}

      {isOpen && results.length === 0 && !isLoading && searchTerm.trim().length >= 2 && (
        <Dropdown ref={dropdownRef}>
          <NoResults>
            No products found for "{searchTerm}"
            <div style={{ fontSize: '13px', marginTop: '8px', color: '#9ca3af' }}>
              Try searching for manufacturer, product name, or model
            </div>
          </NoResults>
        </Dropdown>
      )}

      {isOpen && searchTerm.trim().length > 0 && searchTerm.trim().length < 2 && (
        <Dropdown ref={dropdownRef}>
          <SearchHint>Type at least 2 characters to search</SearchHint>
        </Dropdown>
      )}
    </AutocompleteContainer>
  );
};

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 35px !important;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #242424;
  font-weight: 400;
  line-height: 18px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00727d;
    box-shadow: 0px 4px 12px 0px rgba(0, 114, 125, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const LoadingSpinner = styled.div`
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #00727d;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0px 8px 32px 0px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &.selected {
    background-color: #f8fafc;
    border-left: 3px solid #00727d;
  }
`;

const ProductInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ProductDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
`;

const ProductName = styled.div`
  font-size: 12px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #1f2937;
  font-weight: 600;
  line-height: 16px;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductDetail = styled.div`
  font-size: 12px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #6b7280;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${props => (props.$copied ? '#10b981' : '#00727D')};
  background: transparent !important;
  border: none !important;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  margin: 0 !important;
  padding: 0 !important;
  outline: none !important;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  svg {
    width: 24px;
    height: 24px;
    pointer-events: none;
  }

  &:hover {
    color: ${props => (props.$copied ? '#10b981' : '#005a61')};
    background: transparent !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }

  &:active {
    transform: scale(0.95);
  }

  &:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const CopyFeedback = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1f2937;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1f2937;
  }
`;

const ReferenceLink = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #00727d;
  text-decoration: none;
  background: transparent !important;
  border: none !important;
  transition: all 0.2s ease;
  font-weight: 500;
  margin: 0 !important;
  padding: 0 !important;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: #005a61;
    text-decoration: none;
    background: transparent !important;
  }

  &:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const NoResults = styled.div`
  padding: 20px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-weight: 500;
`;

const SearchHint = styled.div`
  padding: 10px 16px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 12px;
  font-family: 'Segoe UI', Arial, sans-serif;
  text-align: center;
`;

export default ManufacturerProductsAutocomplete;
